import type { Order } from "@/hooks/useOrders";
import { canSendConfirmation, canSendReadyNotification } from "./orderUtils";

/**
 * The order lifecycle, as the operator experiences it during a rush:
 *   confirm  ->  ready  ->  review
 * Only ONE of these is the current next step at any glance. This module is the
 * single source of truth for which one that is, so OrderCard and the By-Time
 * view render an identical primary/secondary hierarchy (DESIGN.md: one confident
 * action over a field of equal-weight buttons).
 */
export type OrderActionKey = "confirm" | "ready" | "review";

/** Every field here is part of the module's public API (see orderActions.test.ts), even when a given view renders only a subset. */
export interface OrderActionState {
  /** This step belongs to this order's lifecycle at all (right type / has phone), whether or not it can be tapped yet. */
  applicable: boolean;
  /** The notification has already been sent. */
  done: boolean;
  /** Can be tapped right now (applicable, not done, not auto-handled). */
  actionable: boolean;
  /** Why an applicable step can't be tapped, for a quiet inline note. */
  blockedReason?: string;
}

export interface OrderActionModel {
  steps: Record<OrderActionKey, OrderActionState>;
  /** Lifecycle order; drives rendering sequence. */
  order: OrderActionKey[];
  /** The one current next action, or null when no step has earned the lead. */
  primaryKey: OrderActionKey | null;
  /**
   * Steps whose notification was actually sent, rendered as quiet chips. Not
   * filtered by current applicability: a notification that went out stays on the
   * record even if the order type later stops making that step relevant.
   */
  doneKeys: OrderActionKey[];
}

interface Options {
  autoConfirmEnabled?: boolean;
}

/**
 * Derive the lifecycle state for an order. Enablement mirrors the pre-existing
 * per-button `disabled` logic exactly, so this is a presentation change, not a
 * behavior change: an action is `actionable` here iff its old button was enabled.
 */
export function getOrderActionModel(order: Order, opts: Options = {}): OrderActionModel {
  const autoConfirm = !!opts.autoConfirmEnabled;
  const hasOffer = !!order.offer_id;
  const hasPhone = !!order.customer_phone;

  // Confirm: right order type + an offer to attach, not already sent, and not
  // handed off to auto-confirmation (which disables the manual control).
  const confirmTypeOk = canSendConfirmation(order);
  const confirmDone = !!order.order_confirmed_notified;
  const confirmAuto = autoConfirm && hasOffer && !confirmDone;
  const confirmActionable = confirmTypeOk && hasOffer && !confirmDone && !confirmAuto;
  const confirmBlocked = confirmTypeOk && !confirmDone && !confirmActionable;

  // Ready: carryout-style orders only.
  const readyApplicable = canSendReadyNotification(order);
  const readyDone = !!order.order_ready_notified;
  const readyActionable = readyApplicable && !readyDone;

  // Review: any order we can text.
  const reviewApplicable = hasPhone;
  const reviewDone = !!order.review_request_sent_notified;
  const reviewActionable = reviewApplicable && !reviewDone;
  // Asking for a review only makes sense once the order has actually been
  // fulfilled. Confirmation marks intake, not hand-off: a delivery order has no
  // ready step, so leading on confirm alone would put "Ask for review" on a card
  // whose food is still in the kitchen.
  const reviewCanLead = !!order.order_ready_notified || !!order.is_posted;

  const steps: Record<OrderActionKey, OrderActionState> = {
    confirm: {
      applicable: confirmTypeOk,
      done: confirmDone,
      actionable: confirmActionable,
      blockedReason: confirmBlocked
        ? confirmAuto
          ? "Auto-confirm is on"
          : !hasOffer
            ? "Needs an offer"
            : undefined
        : undefined,
    },
    ready: {
      applicable: readyApplicable,
      done: readyDone,
      actionable: readyActionable,
    },
    review: {
      applicable: reviewApplicable,
      done: reviewDone,
      actionable: reviewActionable,
      blockedReason: reviewApplicable ? undefined : "No phone number",
    },
  };

  const orderSeq: OrderActionKey[] = ["confirm", "ready", "review"];
  const fulfilmentKey =
    (["confirm", "ready"] as OrderActionKey[]).find((k) => steps[k].actionable) ?? null;
  const primaryKey =
    fulfilmentKey ?? (reviewActionable && reviewCanLead ? ("review" as OrderActionKey) : null);
  const doneKeys = orderSeq.filter((k) => steps[k].done);

  return { steps, order: orderSeq, primaryKey, doneKeys };
}
